export const MARKDOWN_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;

const TOO_LARGE_MESSAGE = 'This Markdown file is too large to preview.';
const UNAVAILABLE_MESSAGE = 'This Markdown file is unavailable.';

export const MarkdownAttachmentSourceTypes = Object.freeze({
  localUpload: 'local-upload',
  remoteUrl: 'remote-url',
});

export function localMarkdownAttachmentSource(uploadId) {
  return { type: MarkdownAttachmentSourceTypes.localUpload, uploadId };
}

export function remoteMarkdownAttachmentSource(url) {
  return { type: MarkdownAttachmentSourceTypes.remoteUrl, url };
}

export function markdownAttachmentSourceForAttachment(attachment) {
  if (attachment?.upload && attachment.upload.state !== 'completed' && attachment?.id) {
    return localMarkdownAttachmentSource(attachment.id);
  }

  return remoteMarkdownAttachmentSource(attachment?.public_url || null);
}

function throwIfAborted(signal) {
  if (!signal?.aborted) return;

  throw new DOMException('The operation was aborted.', 'AbortError');
}

function ensureMarkdownSize(markdown, maxBytes) {
  if (new TextEncoder().encode(markdown).byteLength > maxBytes) throw new Error(TOO_LARGE_MESSAGE);
  return markdown;
}

async function readMarkdownResponse(response, maxBytes) {
  const reader = response.body?.getReader?.();
  if (!reader) return ensureMarkdownSize(await response.text(), maxBytes);

  const decoder = new TextDecoder();
  const markdownChunks = [];
  let bytesRead = 0;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      bytesRead += value.byteLength;
      if (bytesRead > maxBytes) {
        await reader.cancel().catch(() => undefined);
        throw new Error(TOO_LARGE_MESSAGE);
      }

      markdownChunks.push(decoder.decode(value, { stream: true }));
    }

    markdownChunks.push(decoder.decode());
    return markdownChunks.join('');
  } finally {
    reader.releaseLock();
  }
}

async function loadLocalMarkdown(uploadId, { loadLocalUpload, maxBytes, signal }) {
  if (!uploadId || typeof loadLocalUpload !== 'function') throw new Error(UNAVAILABLE_MESSAGE);

  throwIfAborted(signal);
  const file = await loadLocalUpload(uploadId);
  throwIfAborted(signal);

  if (!file || typeof file.text !== 'function') throw new Error(UNAVAILABLE_MESSAGE);
  if (Number.isFinite(file.size) && file.size > maxBytes) throw new Error(TOO_LARGE_MESSAGE);

  const markdown = await file.text();
  throwIfAborted(signal);
  return ensureMarkdownSize(markdown, maxBytes);
}

async function loadRemoteMarkdown(url, { fetchRequest, maxBytes, signal }) {
  if (!url) throw new Error(UNAVAILABLE_MESSAGE);

  const response = await fetchRequest(url, { signal });
  if (!response.ok) throw new Error('The Markdown preview could not be loaded.');

  const contentLength = Number(response.headers?.get?.('content-length'));
  if (Number.isFinite(contentLength) && contentLength > maxBytes) throw new Error(TOO_LARGE_MESSAGE);

  return readMarkdownResponse(response, maxBytes);
}

/**
 * @param {{ type: 'local-upload', uploadId: string } | { type: 'remote-url', url: string }} source
 * @param {{ fetchRequest?: typeof fetch, loadLocalUpload?: (uploadId: string) => Promise<File | Blob | null>, maxBytes?: number, signal?: AbortSignal }} [options]
 */
export async function loadMarkdownAttachment(
  source,
  { fetchRequest = fetch, loadLocalUpload, maxBytes = MARKDOWN_PREVIEW_MAX_BYTES, signal } = {},
) {
  if (source?.type === MarkdownAttachmentSourceTypes.localUpload) {
    return loadLocalMarkdown(source.uploadId, { loadLocalUpload, maxBytes, signal });
  }
  if (source?.type === MarkdownAttachmentSourceTypes.remoteUrl) {
    return loadRemoteMarkdown(source.url, { fetchRequest, maxBytes, signal });
  }

  throw new Error(UNAVAILABLE_MESSAGE);
}
